import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IonButton,
  IonInput,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSpinner
} from "@ionic/react";

import { contractActions } from "../../api/ContractActions";
import { CompanyHeader } from "../../components/CompanyHeader";
import FormFeedback from "../../components/UI/FormFeedback";
import type {
  AddContract,
  ContractIdentifierType
} from "../../types/requestTypes";
import type {
  Company,
  ValidateContractResponse
} from "../../types/responseTypes";
import type { ExceptionDto } from "../../types/schema";
import { normalizeClientCode } from "../../utils/normalizeClientCode";

import styles from "../../pages/4.Contracts/AddContract/AddContractPage.module.scss";

interface AddContractVerifyDetailsProps {
  selectedCompany: Company;
  contractNumber: string;
  setContractNumber: (value: string) => void;
  invoiceNumber: string;
  setInvoiceNumber: (value: string) => void;
  contractAccount: string;
  setContractAccount: (value: string) => void;
  identifierType: ContractIdentifierType;
  setIdentifierType: (value: ContractIdentifierType) => void;
  setContractDetails: (value: ValidateContractResponse) => void;
  setStep?: (value) => void;
}

const AddContractVerifyDetails: React.FC<AddContractVerifyDetailsProps> = ({
  selectedCompany,
  contractNumber,
  setContractNumber,
  invoiceNumber,
  setInvoiceNumber,
  contractAccount,
  setContractAccount,
  identifierType,
  setIdentifierType,
  setContractDetails,
  setStep
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const usesContractAccount = selectedCompany?.id === 1;
  const activeIdentifierType = usesContractAccount
    ? identifierType
    : "invoiceNumber";
  const activeIdentifierValue =
    activeIdentifierType === "contractAccount"
      ? contractAccount
      : invoiceNumber;
  const requestIdentifierValue =
    activeIdentifierType === "contractAccount"
      ? normalizeClientCode(activeIdentifierValue)
      : activeIdentifierValue;

  const submitHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data: AddContract = {
        companyId: selectedCompany?.id,
        contractNumber: contractNumber,
        ...(activeIdentifierType === "invoiceNumber" && requestIdentifierValue
          ? { invoiceNumber: requestIdentifierValue }
          : {}),
        ...(activeIdentifierType === "contractAccount" && requestIdentifierValue
          ? { contractAccount: requestIdentifierValue }
          : {})
      };
      const response = await contractActions.validate(data);
      if (!response.valid) {
        setErrorMsg(t("err.NO_CONTRACT_FOUND"));
      } else {
        setContractDetails(response);
        if (setStep) {
          setStep((prevState) => prevState + 1);
        }
      }
    } catch (err) {
      const ex: ExceptionDto | undefined = err?.response?.data;
      setErrorMsg(
        ex?.message === "PDF_NOT_FOUND" &&
          activeIdentifierType === "invoiceNumber"
          ? t("err.NO_INVOICES_FOR_CONTRACT")
          : t([`err.${ex?.message}`, "err.__"])
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setErrorMsg(null);
  }, [contractNumber, invoiceNumber, contractAccount, identifierType]);

  return (
    <>
      <CompanyHeader company={selectedCompany} />
      <form className={styles.verificationForm} onSubmit={submitHandler}>
        <IonLabel>
          {selectedCompany.id !== 6 // Cod client pentru AQUACARAS
            ? t("contract.no")
            : t("contract.clientCode")}
        </IonLabel>
        <IonInput
          data-testid={"contract-number-input"}
          onIonChange={(e) => setContractNumber(e.target.value as string)}
          value={contractNumber}
          type="text"
          inputmode="text"
          autocomplete="off"
          placeholder={t("placeholder.addHere")}
          className={errorMsg ? "error" : ""}
        />

        {usesContractAccount ? (
          <div className={styles.identifierChoice}>
            <IonLabel>{t("contract.identifyBy")}</IonLabel>
            <IonRadioGroup
              className={styles.identifierOptions}
              value={identifierType}
              onIonChange={(e) =>
                setIdentifierType(e.detail.value as ContractIdentifierType)
              }
            >
              <IonRadio
                className={`${styles.identifierOption} ${
                  identifierType === "invoiceNumber"
                    ? styles.identifierOptionSelected
                    : ""
                }`}
                data-testid="invoice-number-option"
                value="invoiceNumber"
                labelPlacement="end"
              >
                {t("contract.invoiceOption")}
              </IonRadio>
              <IonRadio
                className={`${styles.identifierOption} ${
                  identifierType === "contractAccount"
                    ? styles.identifierOptionSelected
                    : ""
                }`}
                data-testid="contract-account-option"
                value="contractAccount"
                labelPlacement="end"
              >
                {t("contract.accountOption")}
              </IonRadio>
            </IonRadioGroup>
          </div>
        ) : null}

        <IonLabel>
          {activeIdentifierType === "contractAccount"
            ? t("contract.accountNo")
            : t("contract.invoiceNo")}
        </IonLabel>
        <IonInput
          data-testid={`${activeIdentifierType}-input`}
          onIonChange={(e) => {
            if (activeIdentifierType === "contractAccount") {
              setContractAccount(normalizeClientCode(e.target.value as string));
            } else {
              setInvoiceNumber(e.target.value as string);
            }
          }}
          value={activeIdentifierValue}
          type="text"
          inputmode="text"
          autocomplete="off"
          className={errorMsg ? "error" : ""}
          placeholder={t("placeholder.addHere")}
        />

        <IonButton
          className="ag-cta"
          type="submit"
          expand="block"
          fill="solid"
          disabled={!contractNumber || !activeIdentifierValue || isLoading}
        >
          {isLoading ? (
            <>
              <IonSpinner name="circles" /> {t("verifying")}
            </>
          ) : (
            t("verify")
          )}
        </IonButton>

        {errorMsg ? <FormFeedback type={"error"}>{errorMsg}</FormFeedback> : ""}
      </form>
    </>
  );
};

export default AddContractVerifyDetails;
